import React, { useState } from 'react';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';
import { Modal } from '../../ui/Slideover';
import { PencilIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { formatDollars } from '../../../utils/formatCurrency';

/**
 * DiscountSection — Discount management for quotes (REQ-WF-01 / LAT-04)
 *
 * Features:
 * - "+ Add discount" CTA in the Estimate section
 * - Modal: amount field + %/$ toggle (% default) + optional description + Save/Cancel + live preview
 * - On Save: Discount row in Estimate with edit ✎ / remove ×
 * - Bid total drops by discount amount; edit recalcs; remove restores
 */
export function DiscountSection({ discounts = [], onAdd, onUpdate, onRemove, subtotal = 0 }) {
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const handleEdit = (idx) => {
    setEditingIndex(idx);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingIndex(null);
    setShowModal(true);
  };

  const handleSave = (discount) => {
    if (editingIndex !== null) {
      onUpdate(editingIndex, discount);
    } else {
      onAdd(discount);
    }
    setShowModal(false);
    setEditingIndex(null);
  };

  return (
    <div className="space-y-2">
      {/* Existing discounts */}
      {discounts.map((d, idx) => {
        const amount = d.type === 'percent'
          ? (subtotal * d.value / 100)
          : d.value;
        return (
          <div key={idx} className="py-1">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span style={{ color: 'var(--color-success)' }}>
                  Discount
                </span>
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  ({d.type === 'percent' ? `${d.value}%` : formatDollars(d.value)})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium" style={{ color: 'var(--color-success)' }}>
                  -{formatDollars(amount)}
                </span>
              <button
                type="button"
                onClick={() => handleEdit(idx)}
                className="p-1 rounded hover:bg-gray-100"
                aria-label="Edit discount"
              >
                <PencilIcon className="h-3.5 w-3.5 text-gray-400" />
              </button>
              <button
                type="button"
                onClick={() => onRemove(idx)}
                className="p-1 rounded hover:bg-gray-100"
                aria-label="Remove discount"
              >
                <XMarkIcon className="h-3.5 w-3.5 text-gray-400" />
              </button>
            </div>
            </div>
            {d.description && (
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                {d.description}
              </p>
            )}
          </div>
        );
      })}

      {/* Add discount CTA */}
      <button
        type="button"
        onClick={handleAdd}
        className="flex items-center gap-1 text-xs font-medium"
        style={{ color: 'var(--color-action-primary)' }}
      >
        <PlusIcon className="h-3.5 w-3.5" />
        Add discount
      </button>

      {/* Discount Modal */}
      {showModal && (
        <DiscountModal
          open={showModal}
          onClose={() => { setShowModal(false); setEditingIndex(null); }}
          onSave={handleSave}
          initial={editingIndex !== null ? discounts[editingIndex] : null}
          subtotal={subtotal}
        />
      )}
    </div>
  );
}

function DiscountModal({ open, onClose, onSave, initial, subtotal }) {
  const [type, setType] = useState(initial?.type || 'percent');
  const [value, setValue] = useState(initial?.value?.toString() || '');
  const [description, setDescription] = useState(initial?.description || '');

  const numValue = parseFloat(value) || 0;
  const previewAmount = type === 'percent'
    ? (subtotal * numValue / 100)
    : numValue;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!numValue || numValue <= 0) return;
    onSave({ type, value: numValue, description: description.trim() || undefined });
  };

  return (
    <Modal open={open} onClose={onClose} title={initial ? 'Edit Discount' : 'Add Discount'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Type toggle */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Discount Type</label>
          <div className="flex gap-1 p-1 bg-gray-100" style={{ borderRadius: 'var(--radius-md)' }}>
            <button
              type="button"
              onClick={() => setType('percent')}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                type === 'percent' ? 'bg-white ring-1 ring-black/10 text-gray-900' : 'text-gray-600'
              }`}
            >
              %
            </button>
            <button
              type="button"
              onClick={() => setType('fixed')}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                type === 'fixed' ? 'bg-white ring-1 ring-black/10 text-gray-900' : 'text-gray-600'
              }`}
            >
              $
            </button>
          </div>
        </div>

        {/* Amount */}
        <div>
          <label htmlFor="discount-value" className="block text-xs font-medium text-gray-700 mb-1">
            {type === 'percent' ? 'Percentage' : 'Amount'}
          </label>
          <Input
            id="discount-value"
            type="number"
            step="0.01"
            min="0"
            max={type === 'percent' ? '100' : undefined}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={type === 'percent' ? '10' : '500.00'}
            autoFocus
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="discount-desc" className="block text-xs font-medium text-gray-700 mb-1">
            Description (optional)
          </label>
          <Input
            id="discount-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Volume discount, Repeat customer"
          />
        </div>

        {/* Live preview — PR-12 wire QB-2 B7: 3-row block (subtotal / discount / new total) + helper. */}
        {numValue > 0 && (
          <div
            className="p-3 text-sm"
            style={{ background: 'var(--color-surface-alt)', borderRadius: 'var(--radius-md)' }}
          >
            <div className="flex items-center justify-between">
              <span style={{ color: 'var(--color-text-secondary)' }}>Bid subtotal</span>
              <span style={{ color: 'var(--color-text-primary)' }}>{formatDollars(subtotal)}</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span style={{ color: 'var(--color-text-secondary)' }}>
                Discount{type === 'percent' ? ` · ${numValue}%` : ''}
              </span>
              <span style={{ color: 'var(--color-success)' }}>
                –{formatDollars(previewAmount)}
              </span>
            </div>
            <div
              className="flex items-center justify-between mt-2 pt-2 font-medium"
              style={{ borderTop: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              <span>New bid total</span>
              <span>{formatDollars(Math.max(0, subtotal - previewAmount))}</span>
            </div>
            <div
              className="text-xs mt-3 pt-2"
              style={{ color: 'var(--color-text-secondary)', borderTop: '1px dashed var(--color-border)', fontStyle: 'italic' }}
            >
              Applied before tax. Tax recalculated on the reduced amount.
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!numValue || numValue <= 0}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}

export default DiscountSection;
